import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { X, Eye, EyeOff } from "lucide-react";

const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";

export default function SignIn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState(searchParams.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validate = () => {
    const next: Record<string, string> = {};
    if (!email.trim()) next.email = "L'email est requis";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = "Adresse email invalide";
    if (!password) next.password = "Le mot de passe est requis";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    if (!validate()) {
      e.preventDefault();
      return;
    }
    setSubmitting(true);
    // Le formulaire se soumet nativement (POST /users/sign_in → redirection HTTP)
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-md mx-4 bg-background rounded-2xl shadow-2xl border border-border p-8 animate-in zoom-in-95 duration-200">
        {/* Croix → accueil */}
        <button
          onClick={() => navigate("/")}
          className="absolute right-4 top-4 rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
          aria-label="Retour à l'accueil"
        >
          <X className="h-5 w-5" />
        </button>

        {/* En-tête */}
        <div className="text-center space-y-2 mb-6">
          <h1 className="text-2xl font-display bg-gradient-to-r from-[#f7bbac] to-[#f9bb72] bg-clip-text text-transparent">
            Se connecter
          </h1>
          <p className="text-sm text-muted-foreground">
            Connectez-vous à votre compte Wememberz
          </p>
        </div>

        {/* Formulaire — POST natif pour redirection HTTP */}
        <form method="POST" action="/users/sign_in" onSubmit={handleSubmit} className="space-y-4">
          <input type="hidden" name="authenticity_token" value={csrf} />

          <div className="space-y-2">
            <Label htmlFor="email">Adresse email</Label>
            <Input
              id="email"
              name="user[email]"
              type="email"
              placeholder="votre@email.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors({ ...errors, email: "" });
              }}
              className={errors.email ? "border-destructive" : ""}
            />
            {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Mot de passe</Label>
            <div className="relative">
              <Input
                id="password"
                name="user[password]"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors({ ...errors, password: "" });
                }}
                className={errors.password ? "border-destructive" : "pr-10"}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
          </div>

          <Button
            type="submit"
            variant="rainbow"
            size="lg"
            className="w-full font-medium"
            disabled={submitting}
          >
            {submitting ? "Connexion..." : "Se connecter"}
          </Button>
        </form>

        {/* Liens */}
        <div className="mt-6 space-y-3 text-center text-sm">
          <Link
            to="/users/password/new"
            className="block text-[#f7bbac] hover:text-[#f7bbac]/80 transition-smooth underline-offset-4 hover:underline font-medium"
          >
            Mot de passe oublié ?
          </Link>

          <div className="text-muted-foreground">
            Pas encore de compte ?{" "}
            <Link
              to="/users/sign_up"
              className="text-[#f7bbac] hover:text-[#f7bbac]/80 transition-smooth underline-offset-4 hover:underline font-medium"
            >
              S'inscrire
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
